import { currentLocale } from '@/lib/locale';
import { useTranslation } from 'react-i18next';

interface DataPoint {
  date: string;
  effective: number | null;
  affective: number | null;
  perspective: number | null;
}

interface Props {
  data: DataPoint[];
}

const W = 600;
const H = 200;
const PAD = { top: 24, right: 24, bottom: 44, left: 36 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

const SERIES = [
  { key: 'effective' as const, color: '#22c55e' },
  { key: 'affective' as const, color: '#8b5cf6' },
  { key: 'perspective' as const, color: '#f59e0b' },
];

const Y_TICKS = [0, 2, 4, 6, 8, 10];

function toX(i: number, n: number) {
  return PAD.left + (n <= 1 ? PLOT_W / 2 : (i / (n - 1)) * PLOT_W);
}

function toY(score: number) {
  return PAD.top + (1 - score / 10) * PLOT_H;
}

function formatDate(dateStr: string) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit' });
}

function buildPath(points: Array<{ x: number; y: number } | null>) {
  const segments: string[] = [];
  let inLine = false;
  for (const p of points) {
    if (!p) { inLine = false; continue; }
    if (!inLine) { segments.push(`M ${p.x} ${p.y}`); inLine = true; }
    else { segments.push(`L ${p.x} ${p.y}`); }
  }
  return segments.join(' ');
}

export function ScoreLineChart({ data }: Props) {
  const n = data.length;
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-semibold">{t('performance.score_chart.evolution_title')}</h3>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ overflow: 'visible' }}>
        {Y_TICKS.map(v => {
          const y = toY(v);
          return (
            <g key={v}>
              <line
                x1={PAD.left} y1={y} x2={PAD.left + PLOT_W} y2={y}
                stroke="hsl(var(--border))" strokeWidth={0.8}
              />
              <text
                x={PAD.left - 6} y={y + 4}
                textAnchor="end" fontSize={10}
                fill="hsl(var(--muted-foreground))"
                fontFamily="Inter, sans-serif"
              >
                {v}
              </text>
            </g>
          );
        })}

        {data.map((d, i) => (
          <text
            key={i}
            x={toX(i, n)}
            y={PAD.top + PLOT_H + 16}
            textAnchor="middle" fontSize={9}
            fill="hsl(var(--muted-foreground))"
            fontFamily="Inter, sans-serif"
          >
            {formatDate(d.date)}
          </text>
        ))}

        {SERIES.map(({ key, color }) => {
          const points = data.map((d, i) =>
            d[key] !== null ? { x: toX(i, n), y: toY(d[key]!) } : null
          );
          const path = buildPath(points);
          return (
            <g key={key}>
              {path && (
                <path
                  d={path} fill="none" stroke={color}
                  strokeWidth={2.2} strokeLinejoin="round" strokeLinecap="round"
                />
              )}
              {points.map((p, i) =>
                p ? (
                  <g key={i}>
                    <circle cx={p.x} cy={p.y} r={4} fill={color} />
                    <text
                      x={p.x} y={p.y - 8}
                      textAnchor="middle" fontSize={9.5} fontWeight={600}
                      fill={color} fontFamily="Inter, sans-serif"
                    >
                      {data[i][key]!.toFixed(1)}
                    </text>
                  </g>
                ) : null
              )}
            </g>
          );
        })}
      </svg>

      <div className="flex items-center justify-center gap-5 flex-wrap">
        {SERIES.map(({ key, color }) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-[11px] text-muted-foreground">{t(`performance.score_chart.${key}`)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
